import { FC } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import ISTData from '@models/ISTData';
import { formatDate } from '../../shared/functions';

export interface VignettePublicationCardProps {
  publication: ISTData;
}

/** Union type for author data that may be strings or objects with name property */
type AuthorLike = string | { name?: string };

/**
 * Extracts author names from an array that may contain strings or author objects.
 * Handles cases where API returns author objects instead of plain strings.
 */
const extractAuthorNames = (authors: AuthorLike[]): string[] =>
  authors
    .map((author) => {
      if (typeof author === 'string') {
        return author;
      }
      if (author && typeof author === 'object') {
        const { name } = author;
        return typeof name === 'string' ? name : '';
      }
      return '';
    })
    .filter((name) => name.length > 0);

export const VignettePublicationCard: FC<VignettePublicationCardProps> = ({
  publication,
}) => {
  const router = useRouter();

  const handleTitleClick = () => {
    router.push(`/publication/${encodeURIComponent(publication.id)}`);
  };

  const getAuthorsDisplay = (): string | null => {
    if (Array.isArray(publication.authors) && publication.authors.length > 0) {
      const authorNames = extractAuthorNames(publication.authors);
      if (authorNames.length > 0) {
        const overflow =
          authorNames.length > 3 ? ` +${authorNames.length - 3} more` : '';
        return authorNames.slice(0, 3).join(', ') + overflow;
      }
    }
    if (
      Array.isArray(publication.authors_in_chinese) &&
      publication.authors_in_chinese.length > 0
    ) {
      const authorNames = extractAuthorNames(publication.authors_in_chinese);
      if (authorNames.length > 0) {
        const overflow =
          authorNames.length > 3 ? ` +${authorNames.length - 3} more` : '';
        return authorNames.slice(0, 3).join(', ') + overflow;
      }
    }
    return null;
  };
  const authorsDisplay = getAuthorsDisplay();

  const affiliationsDisplay =
    publication.affiliations?.length > 0
      ? publication.affiliations.slice(0, 2).join('; ') +
        (publication.affiliations.length > 2
          ? ` +${publication.affiliations.length - 2} more`
          : '')
      : null;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(148, 163, 184, 0.12)',
        background: 'rgba(2, 6, 23, 0.42)',
        p: 1.75,
        transition: 'border-color 0.18s',
        '&:hover': {
          borderColor: 'rgba(99, 102, 241, 0.35)',
        },
      }}
    >
      <Stack spacing={0.75}>
        {/* Date */}
        {publication.published_date && (
          <Typography
            sx={{
              fontSize: '0.72rem',
              color: '#64748b',
              fontWeight: 500,
              letterSpacing: '0.04em',
            }}
          >
            {formatDate(publication.published_date)}
          </Typography>
        )}

        {/* Title */}
        <Typography
          onClick={handleTitleClick}
          sx={{
            fontSize: '0.88rem',
            fontWeight: 600,
            color: '#c7d2fe',
            lineHeight: 1.45,
            cursor: 'pointer',
            '&:hover': { color: '#a5b4fc', textDecoration: 'underline' },
          }}
        >
          {publication.title}
        </Typography>

        {/* Authors */}
        {authorsDisplay && (
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: '#94a3b8',
              lineHeight: 1.4,
            }}
            noWrap
          >
            {authorsDisplay}
          </Typography>
        )}

        {/* Affiliations */}
        {affiliationsDisplay && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#64748b',
              lineHeight: 1.35,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={publication.affiliations?.join('; ')}
          >
            {affiliationsDisplay}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
